import React, { ReactNode } from "react";
import { useAuth } from "@noom/hive-auth";
import {
  getSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import { NoomLoader } from "src/components/noom-loader/NoomLoader";
import { trackEvent } from "./api/tracker";
import { captureMessage } from "@sentry/browser";

export const IDENTITY_SERVER_CALLBACK_PATH = "/p/login/callback";
export const IDENTITY_SERVER_LOGIN_PATH = "/p/login";
export const POST_AUTH_PATH = "/p/login/post-auth";

export type InternalLocation = Pick<Location, "pathname" | "search" | "hash">;

export const POST_AUTH_INTERNAL_LOCATION: InternalLocation = {
  pathname: POST_AUTH_PATH,
  search: "",
  hash: "",
};

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    loginInternalLocation?: InternalLocation;
    loginRedirectCount?: number;
    loginRedirectTimestamp?: number;
    postAuthAnimationEnabled?: boolean;
  }
}

export function getLoginInternalLocation(): InternalLocation | undefined {
  const { loginInternalLocation } = getSessionState("browser");
  return loginInternalLocation;
}

export function clearLoginInternalLocation() {
  updateSessionState("browser", {
    loginInternalLocation: undefined,
  });
}

export function isPostAuthAnimationEnabled(): boolean {
  return !!getSessionState("browser").postAuthAnimationEnabled;
}

export function clearPostAuthAnimationFlag() {
  updateSessionState("browser", {
    postAuthAnimationEnabled: undefined,
  });
}

export function enablePostAuthAnimation() {
  updateSessionState("browser", {
    postAuthAnimationEnabled: true,
  });
}

export function redirectToLogin(location: InternalLocation = window.location) {
  const loginRedirectCount = getSessionState("browser").loginRedirectCount || 0;
  updateSessionState("browser", {
    loginRedirectCount: loginRedirectCount + 1,
    loginRedirectTimestamp: Date.now(),
    loginInternalLocation: {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    },
  });
  window.location.replace(IDENTITY_SERVER_LOGIN_PATH);
}

export function trackAndRedirectToLogin() {
  const { loginRedirectTimestamp, loginRedirectCount } =
    getSessionState("browser");
  if (loginRedirectCount) {
    captureMessage("Multiple login redirects detected", "warning");
  }
  trackEvent("BuyflowAuthGuardRedirectToLogin", {
    previousLoginRedirectCount: loginRedirectCount ?? 0,
    msSinceLastLoginRedirect: Date.now() - (loginRedirectTimestamp ?? 0),
  });
  redirectToLogin();
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, shouldReLogin } = useAuth();

  if (!isAuthenticated || (isAuthenticated && shouldReLogin())) {
    trackAndRedirectToLogin();
    return <NoomLoader fullScreen />;
  }
  return <>{children}</>;
}
